import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

type MessageContent = JSX.Element | string | undefined
type MessageSeverity = 'success' | 'error' | 'warning' | 'info'

type Nessage = {
  content: MessageContent
  severity: MessageSeverity
}

type MessageOptions = {
  timeout?: number,
}

interface ProviderValue {
  message?: Nessage
  showMessage: (
    message?: JSX.Element | string,
    severity?: MessageSeverity,
    options?: MessageOptions
  ) => void
  hideMessage: () => void
  banner?: Nessage
  showBanner: (
    message?: JSX.Element | string,
    severity?: MessageSeverity,
  ) => void
  hideBanner: () => void
}

export const GlobalMessage = createContext<ProviderValue>({
  message: undefined,
  showMessage: () => undefined,
  hideMessage: () => undefined,
  showBanner: () => undefined,
  hideBanner: () => undefined,
})

type Props = {
  children?: React.ReactNode
}

export const GlobalMessageProvider: React.FC<Props> = ({
  children,
}) => {
  const [message, setMessage] = useState<Nessage>()
  const [banner, setBanner] = useState<Nessage>()

  const showMessage = useCallback((
    content: MessageContent, severity: MessageSeverity = 'success', options?: MessageOptions
  ) => {
    const { timeout = 5000 } = options || {}
    setMessage({ content, severity })
    setTimeout(() => {
      setMessage(undefined)
    }, timeout)
  }, [])

  const showBanner = useCallback((
    content: MessageContent, severity: MessageSeverity = 'success'
  ) => {
    setBanner({ content, severity })
  }, [])

  const hideMessage = useCallback(() => {
    setMessage(undefined)
  }, [])

  const hideBanner = useCallback(() => {
    setBanner(undefined)
  }, [])

  const value = useMemo(() => (
    {
      message,
      showMessage,
      hideMessage,
      banner,
      showBanner,
      hideBanner,
    }
  ), [message, showMessage, hideMessage, banner, showBanner, hideBanner])

  return (
    <GlobalMessage.Provider value={value}>
      {children}
    </GlobalMessage.Provider>
  )
}

export const useGlobalMessage = (): ProviderValue => useContext(GlobalMessage)
